import React, { useState } from "react";
import api from "../utils/api";
import {
  CloudArrowUpIcon,
  DocumentArrowDownIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";

function INRBulkUpload({ onUploadSuccess }) {
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [errors, setErrors] = useState([]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setMessage("");
    setUploadResult(null);
    setErrors([]);

    // Validate file type
    if (selectedFile) {
      const allowedTypes = [".csv", ".xls", ".xlsx", ".json"];
      const fileExtension = selectedFile.name
        .toLowerCase()
        .substring(selectedFile.name.lastIndexOf("."));

      if (!allowedTypes.includes(fileExtension)) {
        setMessage(
          "Invalid file type. Please select a CSV, XLS, XLSX, or JSON file."
        );
        setFile(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setMessage("Please select a file to upload.");
      return;
    }

    setIsLoading(true);
    setMessage("");
    setUploadResult(null);
    setErrors([]);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await api.post("/api/inr-bulk-upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setUploadResult(response.data);

      if (response.data.success) {
        setMessage(
          `✅ Upload successful! ${response.data.summary.successful} INR investor records imported.`
        );
        // Call the success callback to refresh the parent component
        if (onUploadSuccess) {
          onUploadSuccess();
        }
      } else {
        setMessage(`⚠️ Upload completed with issues. Check the details below.`);
      }

      if (response.data.errors) {
        setErrors(response.data.errors);
      }
    } catch (err) {
      console.error("INR Upload error:", err);
      const errorMessage =
        err.response?.data?.message || "Upload failed due to server error";
      setMessage(`❌ ${errorMessage}`);

      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      }

      setUploadResult(err.response?.data || null);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadTemplate = async (format) => {
    try {
      setMessage(`Downloading ${format.toUpperCase()} template...`);

      const response = await api.get(
        `/api/inr-bulk-upload/template/${format}`,
        {
          responseType: "blob",
        }
      );

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `inr_investors_template.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setMessage(
        `✅ ${format.toUpperCase()} template downloaded successfully!`
      );
    } catch (error) {
      console.error("Template download error:", error);
      setMessage(`❌ Failed to download ${format.toUpperCase()} template`);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload Form */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center mb-4">
            <CloudArrowUpIcon className="h-6 w-6 text-blue-500 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">
              Upload INR Investor File
            </h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select File (CSV, XLS, XLSX, JSON)
              </label>
              <input
                type="file"
                accept=".csv,.xls,.xlsx,.json"
                onChange={handleFileChange}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {file && (
                <p className="mt-2 text-sm text-gray-600">
                  Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>

            <button
              onClick={handleUpload}
              disabled={!file || isLoading}
              className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Uploading...
                </>
              ) : (
                <>
                  <CloudArrowUpIcon className="h-5 w-5 mr-2" />
                  Upload INR Data
                </>
              )}
            </button>
          </div>

          {/* File Format Info */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <div className="flex">
              <InformationCircleIcon className="h-5 w-5 text-blue-400 mr-2 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-blue-800">
                  INR File Requirements
                </h4>
                <ul className="mt-2 text-sm text-blue-700 list-disc list-inside space-y-1">
                  <li>Supported formats: CSV, XLS, XLSX, JSON</li>
                  <li>Maximum file size: 5 MB</li>
                  <li>
                    <strong>Required fields:</strong> date, name
                  </li>
                  <li>
                    <strong>Optional fields:</strong> phone_number, received,
                    sent, balance, remarks
                  </li>
                  <li>
                    Date format: YYYY-MM-DD (e.g., 2025-09-25) or DD-MM-YYYY or
                    MM/DD/YYYY
                  </li>
                  <li>
                    Financial amounts: Use positive numbers for received/sent
                    amounts
                  </li>
                  <li>
                    Phone format: Numbers with +, -, (), spaces allowed (e.g.,
                    +91-9876543210)
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Download Templates */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center mb-4">
            <DocumentArrowDownIcon className="h-6 w-6 text-green-500 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">
              Download INR Templates
            </h3>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Download sample template files to see the correct format for INR
            investor data.
          </p>

          <div className="space-y-3">
            <button
              onClick={() => downloadTemplate("csv")}
              className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <DocumentArrowDownIcon className="h-5 w-5 mr-2 text-green-500" />
              Download CSV Template
            </button>

            <button
              onClick={() => downloadTemplate("xlsx")}
              className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <DocumentArrowDownIcon className="h-5 w-5 mr-2 text-green-500" />
              Download Excel Template
            </button>

            <button
              onClick={() => downloadTemplate("json")}
              className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <DocumentArrowDownIcon className="h-5 w-5 mr-2 text-green-500" />
              Download JSON Template
            </button>
          </div>

          {/* Sample Data Preview */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-800 mb-2">
              Sample Data Structure:
            </h4>
            <pre className="text-xs text-gray-600 overflow-x-auto">
              {`date: 2025-09-25 (YYYY-MM-DD format)
name: John Doe (investor name)
phone_number: +91-9876543210 (optional)
received: 50000 (amount received)
sent: 25000 (amount sent)
balance: 25000 (current balance)
remarks: Initial investment (optional notes)`}
            </pre>
          </div>
        </div>
      </div>

      {/* Status Message */}
      {message && (
        <div
          className={`p-4 rounded-md ${
            message.includes("✅")
              ? "bg-green-50 border border-green-200"
              : message.includes("⚠️")
              ? "bg-yellow-50 border border-yellow-200"
              : message.includes("❌")
              ? "bg-red-50 border border-red-200"
              : "bg-blue-50 border border-blue-200"
          }`}
        >
          <p
            className={`text-sm ${
              message.includes("✅")
                ? "text-green-700"
                : message.includes("⚠️")
                ? "text-yellow-700"
                : message.includes("❌")
                ? "text-red-700"
                : "text-blue-700"
            }`}
          >
            {message}
          </p>
        </div>
      )}

      {/* Upload Results */}
      {uploadResult && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Upload Results
          </h3>

          {/* Summary */}
          {uploadResult.summary && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-blue-700">
                  Total Records
                </div>
                <div className="text-2xl font-bold text-blue-900">
                  {uploadResult.summary.total}
                </div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-green-700">
                  Successful
                </div>
                <div className="text-2xl font-bold text-green-900">
                  {uploadResult.summary.successful}
                </div>
              </div>
              <div className="bg-red-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-red-700">Failed</div>
                <div className="text-2xl font-bold text-red-900">
                  {uploadResult.summary.failed}
                </div>
              </div>
            </div>
          )}

          {/* Errors */}
          {errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-red-800">
                    Validation Errors
                  </h4>
                  <div className="mt-2 text-sm text-red-700">
                    <ul className="list-disc list-inside space-y-1 max-h-64 overflow-y-auto">
                      {errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Success Message */}
          {uploadResult.success && uploadResult.summary?.successful > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex">
                <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-green-800">
                    Upload Successful
                  </h4>
                  <p className="mt-1 text-sm text-green-700">
                    {uploadResult.summary.successful} INR investor records have
                    been successfully imported to the database.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default INRBulkUpload;
